import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import PipelineSummaries from "../PipelineSummaries";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { CustomButton } from "../../../components/common/customButton";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { exportSummaryToExcel } from "../../../services/summaryExportService";
import type { Status } from "../../../types/deal";

export function ChartsTab() {
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [isCondensed, setIsCondensed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { status } = useReferenceLists();

  const statusIds = useMemo(
    () => statusFilter.map((s) => s.id),
    [statusFilter],
  );

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      await exportSummaryToExcel(statusIds, statusFilter);
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to export to Excel. Please try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
          gap: 2,
          marginTop: "12px",
        }}
      >
        <CustomMultiSelect
          value={statusFilter.map((s) => s.id)}
          onChange={(e) => {
            const selectedIds = e.target.value as (string | number)[];
            const selectedStatuses = status.filter((opt: Status): opt is Status =>
              selectedIds.includes(opt.id),
            );
            setStatusFilter(selectedStatuses);
          }}
          options={status}
          placeholder={`Select Status`}
          labelKey={"status"}
          minWidth={"200px"}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            flexWrap: "nowrap",
            marginLeft: "auto",
          }}
        >
          <CustomButton
            handleButtonPress={() => setIsCondensed((prev) => !prev)}
            isButtonDisabled={false}
            text={isCondensed ? "Condensed" : "Expanded"}
            isLoading={false}
            isLoadingText=""
            solid={isCondensed}
            widthOverride="100px"
          />
          <CustomButton
            handleButtonPress={handleExportToExcel}
            isButtonDisabled={isExporting}
            text="Export to Excel"
            isLoading={isExporting}
            isLoadingText="Exporting..."
            solid={false}
            widthOverride="140px"
          />
        </Box>
      </Box>

      <Box sx={{ width: "100%", minWidth: 0 }}>
        <PipelineSummaries
          groupBy="status"
          statusIds={statusIds}
          condensed={isCondensed}
        />
        <PipelineSummaries
          groupBy="sector"
          statusIds={statusIds}
          condensed={isCondensed}
        />
        <PipelineSummaries groupBy="country" statusIds={statusIds} />
        <PipelineSummaries groupBy="kill_reason" statusIds={statusIds} />
      </Box>
    </Box>
  );
}
